import { useEffect } from "react";
import { MapContainer, TileLayer, useMap, ZoomControl } from "react-leaflet";
import ConstantsMap from "../../../constants/ConstantsMap";
import { wmResolution } from "../../../constants/MapCRS";
import WidgetMapLayerToggle from "../widget-map-layer-toggle/WidgetMapLayerToggle";

// Carto (WebMercator) – limit maximum zoom-out to 9
const CARTO_ZOOM_MIN = 9;

function MapRefSetter({ setMapRef, fitMapBounds, skipFitBounds }) {
  const map = useMap();
  useEffect(() => {
    setMapRef(map);
    if (typeof fitMapBounds === "function" && !skipFitBounds) {
      fitMapBounds();
    }
    return () => setMapRef(null);
  }, [map, setMapRef, fitMapBounds, skipFitBounds]);
  return null;
}

function isFiniteCoord(lat, lng) {
  return typeof lat === "number" && Number.isFinite(lat) && typeof lng === "number" && Number.isFinite(lng);
}

/**
 * Keeps Leaflet map layout in sync when the container size changes
 * (e.g. when collapsing / expanding the dashboard and changing map width).
 * Uses ResizeObserver on the map container and calls invalidateSize()
 * without changing zoom/center.
 */
function MapResizeObserver() {
  const map = useMap();
  useEffect(() => {
    const container = map.getContainer();
    if (!container || typeof ResizeObserver === "undefined") return;

    const resizeObserver = new ResizeObserver(() => {
      map.invalidateSize(false);
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [map]);

  return null;
}

function SyncMapState({ onMapStateChange }) {
  const map = useMap();
  useEffect(() => {
    if (typeof onMapStateChange !== "function") return;
    const save = () => {
      const center = map.getCenter();
      const lat = center?.lat;
      const lng = center?.lng;
      if (!isFiniteCoord(lat, lng)) return;
      const z = map.getZoom();
      const resolution = wmResolution(z, lat);
      if (typeof resolution !== "number" || !Number.isFinite(resolution) || resolution <= 0) return;
      onMapStateChange({ center: { lat, lng }, resolution });
    };
    map.on("moveend", save);
    map.on("zoomend", save);
    return () => {
      map.off("moveend", save);
      map.off("zoomend", save);
    };
  }, [map, onMapStateChange]);
  return null;
}

/**
 * Shared map shell: wrapper div + MapContainer + tile layers + zoom + layer toggle.
 * Pass map content (markers, popups, etc.) as children.
 */
function WidgetMapLayout({
  containerRef,
  className,
  style,
  onClick,
  center,
  zoom: zoomProp,
  setMapRef,
  fitMapBounds,
  skipFitBounds = false,
  onMapStateChange,
  mapType,
  setMapType,
  children,
}) {
  const safeCenter = Array.isArray(center) && center.length >= 2 ? [Number(center[0]), Number(center[1])] : [32.0853, 34.7818];
  const lat = safeCenter[0];
  const lng = safeCenter[1];
  const centerIsFinite = Number.isFinite(lat) && Number.isFinite(lng);
  const finalCenter = centerIsFinite ? safeCenter : [32.0853, 34.7818];
  const zoomNum = zoomProp != null && Number.isFinite(Number(zoomProp)) ? Number(zoomProp) : ConstantsMap.DEFAULT_ZOOM;
  const zoom = Math.max(CARTO_ZOOM_MIN, Math.min(ConstantsMap.MAX_ZOOM, Math.round(zoomNum * 100) / 100));

  const mapOptions = {
    center: finalCenter,
    zoom,
    zoomControl: false,
    minZoom: CARTO_ZOOM_MIN,
    zoomSnap: mapType === "carto" ? 0 : 1,
    zoomDelta: mapType === "carto" ? 0.5 : 1,
    maxZoom: ConstantsMap.MAX_ZOOM,
  };

  return (
    <div ref={containerRef} className={className} style={style} onClick={onClick}>
      <MapContainer key={mapType} {...mapOptions} style={{ height: "100%", width: "100%", borderRadius: "1.5rem", overflow: "hidden" }}>
        <MapResizeObserver />
        <MapRefSetter setMapRef={setMapRef} fitMapBounds={fitMapBounds} skipFitBounds={skipFitBounds} />
        {mapType === "carto" && <TileLayer url={ConstantsMap.TILE_LAYER_CARTO} attribution={ConstantsMap.ATTRIBUTION_CARTO} />}
        {mapType === "orthophoto" && <TileLayer url={ConstantsMap.TILE_LAYER_ORTHO} attribution={ConstantsMap.ATTRIBUTION_ORTHO} />}
        <ZoomControl position="topright" />
        {typeof onMapStateChange === "function" && <SyncMapState onMapStateChange={onMapStateChange} />}
        {children}
        <WidgetMapLayerToggle mapType={mapType} setMapType={setMapType} />
      </MapContainer>
    </div>
  );
}

export default WidgetMapLayout;
